/**
 * IRI <-> prefixed-name compaction. Seeded with a well-known fallback table
 * (so IRIs from datasets that don't declare their own prefix for a term's
 * namespace — e.g. cross-references into `xsd:` or `dcterms:` — still
 * compact sensibly), then extended with every `@prefix` declaration N3.js
 * reports while parsing each bundled file.
 */

const WELL_KNOWN_PREFIXES: Record<string, string> = {
  rdf: "http://www.w3.org/1999/02/22-rdf-syntax-ns#",
  rdfs: "http://www.w3.org/2000/01/rdf-schema#",
  owl: "http://www.w3.org/2002/07/owl#",
  xsd: "http://www.w3.org/2001/XMLSchema#",
  sh: "http://www.w3.org/ns/shacl#",
  prov: "http://www.w3.org/ns/prov#",
  dcterms: "http://purl.org/dc/terms/",
  dc: "http://purl.org/dc/elements/1.1/",
  skos: "http://www.w3.org/2004/02/skos/core#",
  vann: "http://purl.org/vocab/vann/",
  foaf: "http://xmlns.com/foaf/0.1/",
  qudt: "http://qudt.org/schema/qudt/",
  unit: "http://qudt.org/vocab/unit/",
  quantitykind: "http://qudt.org/vocab/quantitykind/",
  "qudt-sample": "https://ontologies.neo-data-models.dev/qudt-sample#",
  saref: "https://saref.etsi.org/core/",
  s4ener: "https://saref.etsi.org/saref4ener/",
  bfo: "http://purl.obolibrary.org/obo/",
};

export class PrefixTable {
  /** namespace IRI -> prefix, longest-namespace-first for greedy longest-match compaction. */
  private byNamespaceDesc: Array<[string, string]> = [];
  /** prefix -> namespace IRI, for expansion. */
  private byPrefix = new Map<string, string>();

  constructor(seed: Record<string, string> = WELL_KNOWN_PREFIXES) {
    for (const [prefix, namespaceIri] of Object.entries(seed)) {
      this.register(prefix, namespaceIri);
    }
  }

  register(prefix: string, namespaceIri: string): void {
    if (prefix === "" || this.byPrefix.has(prefix)) {
      // Empty/base prefixes and re-declarations of an already-known prefix
      // (common across files that all declare `rdf:`/`owl:`) are skipped —
      // the first registration wins, keeping the table small and stable.
      if (this.byPrefix.has(prefix)) return;
    }
    this.byPrefix.set(prefix, namespaceIri);
    this.byNamespaceDesc.push([namespaceIri, prefix]);
    this.byNamespaceDesc.sort((a, b) => b[0].length - a[0].length);
  }

  /** Compact a full IRI to `prefix:localName`, or return the IRI unchanged if no namespace matches. */
  compact(iri: string): string {
    for (const [namespaceIri, prefix] of this.byNamespaceDesc) {
      if (iri.startsWith(namespaceIri) && iri.length > namespaceIri.length) {
        return `${prefix}:${iri.slice(namespaceIri.length)}`;
      }
    }
    return iri;
  }

  /** Expand a `prefix:localName` string back to a full IRI. Returns null if the prefix is unregistered. */
  expand(prefixedName: string): string | null {
    const colonIndex = prefixedName.indexOf(":");
    if (colonIndex === -1) return null;
    const prefix = prefixedName.slice(0, colonIndex);
    const namespaceIri = this.byPrefix.get(prefix);
    if (!namespaceIri) return null;
    return namespaceIri + prefixedName.slice(colonIndex + 1);
  }
}
